import { AntPopover, AntTooltip, Icons, Progress, Text } from '@agro-club/agroclub-shared'
import { DASH } from 'modules/constants'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import { endpoints } from 'modules/endpoints'
import { formatQuantity } from 'modules/utils/numbers/formatQuantity'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton } from 'views/components/Analytics'
import { CellRenders } from 'views/components/Table/renderers'
import { TtnDocumentShort } from 'views/pages/LogisticsKR/types'
import { UploadTtnButton } from '../UploadTtnButton/UploadTtnButton'
import { PopoverContent } from './PopoverContent'

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 4px;
`

const CommentIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.color.secondary16a};
  }

  svg {
    width: 13px;
    height: 13px;
    path {
      fill: ${({ theme }) => theme.color.textPrimary900};
    }
  }
`

const TooltipContentWrapper = styled.div`
  min-width: 200px;
  max-width: 500px;
  white-space: pre-line;
`

const loadKeys = {
  ttnFilesType: 'has_ttn1_files',
  ttnRejectFilesType: 'has_ttn1_rejection_reason',
  quantityType: 'load_quantity',
  dateType: 'load_date',
  fetchFilter: 'ttn_1',
} as const

const unloadKeys = {
  ttnFilesType: 'has_ttn2_files',
  ttnRejectFilesType: 'has_ttn2_rejection_reason',
  quantityType: 'unload_quantity',
  dateType: 'unload_date',
  fetchFilter: 'ttn_2',
} as const

type Props = {
  trip: any
  loadType: 'load' | 'unload'
  onSuccess: (() => void) | refetchFunc
  externallyDeletedDocId?: string
}

export const LoadUploadCell: FC<Props> = ({ loadType, trip, onSuccess, externallyDeletedDocId }) => {
  const { t } = useTranslation('logisticsKR')
  const [filePopoverVisible, setFilePopoverVisible] = useState(false)
  const [isReadyToFetch, setIsReadyToFetch] = useState(false)
  const keys = loadType === 'load' ? loadKeys : unloadKeys
  const [docsProgress, docs = [], docsRefetch] = useSingleEntity<TtnDocumentShort[]>(
    isReadyToFetch ? endpoints.LogisticsKR.tripDocuments(trip.id, keys.fetchFilter) : undefined,
  )
  const docsIsLoaded = docsProgress === Progress.SUCCESS

  const docUploadOnSuccess = async () => {
    await onSuccess()

    if (docsIsLoaded) {
      docsRefetch(true)
    }
  }

  const docsDeleteOnSuccess = async () => {
    await docsRefetch(true)
    onSuccess()
  }

  const popoverOpenChangeHandler = () => {
    setFilePopoverVisible((prev) => !prev)

    if (!isReadyToFetch) {
      setIsReadyToFetch(true)
    }
  }

  useEffect(() => {
    if (!externallyDeletedDocId) return

    const externallyDeletedDocument = docs.find((doc) => String(doc.id) === externallyDeletedDocId)

    if (externallyDeletedDocument && docsIsLoaded) {
      docsRefetch()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externallyDeletedDocId])

  const comment = docs.find((doc) => doc.comment)?.comment

  return (
    <div>
      {CellRenders.date(trip[keys.dateType])}
      <Text style={{ marginBottom: '4px' }} typography="accentMedium">
        {trip[keys.quantityType] ? formatQuantity(trip[keys.quantityType]) : DASH}
      </Text>
      <ButtonsWrapper>
        <UploadTtnButton loadType={loadType} tripId={trip.id} onSuccess={docUploadOnSuccess} />
        {trip[keys.ttnFilesType] && (
          <AntPopover
            content={
              <PopoverContent
                hasErrorTtn={trip[keys.ttnRejectFilesType]}
                tripId={trip.id}
                progress={docsProgress}
                documents={docs}
                onSuccess={docsDeleteOnSuccess}
              />
            }
            placement="bottomRight"
            trigger="click"
            open={filePopoverVisible}
            onOpenChange={popoverOpenChangeHandler}
          >
            <AButton id="popoverTtnList" color="secondary" size="small" Icon={Icons.IconAttach} />
          </AntPopover>
        )}
        {comment && (
          <AntTooltip tooltipContent={<TooltipContentWrapper>{comment}</TooltipContentWrapper>} variant="secondary">
            <CommentIconWrapper>
              <Icons.IconNegotiations />
            </CommentIconWrapper>
          </AntTooltip>
        )}
      </ButtonsWrapper>
      {trip[keys.ttnFilesType] && !trip[keys.ttnRejectFilesType] && (
        <Text typography="bodySmall" color="secondary">
          {t('details.file_uploaded')}
        </Text>
      )}
      {trip[keys.ttnRejectFilesType] && (
        <Text typography="bodySmall" color="accent">
          {t('common:hasError')}
        </Text>
      )}
    </div>
  )
}
